import React, { useEffect, useState } from "react";
import { Box, Typography, useTheme, Stack, IconButton } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import RemarksDialog from "../components/TaskDetails/RemarksDialog";
import {
  TaskDetailsApi,
  ActivateFlag,
  WorkerRemarkUpdate,
  PMRemarkStatusUpdate,
  AxiosResponse,
} from "../utils/urlConst";
import LocalStorageUtil from "../utils/localStorageUtil";
import { storageKeys } from "../utils/constants";
import apiService from "../apiService/ApiService";
import PMRemarkDialog from "../components/TaskDetails/PMRemarkDialog";
import ImageWithZoomAndDrag from "../components/TaskDetails/ImageWithZoomAndDrag";
import CommonLoader from "../components/CommonLoader";
import { useAlert } from "../contexts/AlertContext";
import { useTranslation } from "react-i18next";
import useResponsive from "../hooks/useResponsive";
import { ProjectDetails } from "../components/TaskDetails/ProjectDetails";
import { Flag } from "@mui/icons-material";

import {
  ProjectData,
  Tool,
  Material,
  Medewerker,
} from "../interfaces/TaskDetails";
import { MaterialCard } from "../components/TaskDetails/MaterialCard";
import { DocumentCard } from "../components/TaskDetails/DocumentCard";
import { RemarksCard } from "../components/TaskDetails/RemarksCard";
import TimerProjectSelectionModal from "../components/Timer/TimerProjectSelectionModal";
import { useProjectContext } from "../contexts/ProjectContext";
import { useTimerContext } from "../contexts/TimerContext";
import InfoPopover from "../components/TaskDetails/InfoPopover";
import DailyRemarkDialog from "../components/TaskDetails/DailyRemarkDialog";

const TaskDetailScreen: React.FC = () => {

  const { t } = useTranslation();
  const navigate = useNavigate();

  const [openDialog, setOpenDialog] = useState(false);
  const [openPMRemarkDialog, setOpenPMRemarkDialog] = useState(false);
  const theme = useTheme();

  const [isLoading, setIsLoading] = useState(true);
  const [isFlag, setIsFlag] = useState<boolean | null>(null);
  const [projectData, setProjectData] = useState<ProjectData | null>(null);
  const [tool, setTool] = useState<Tool[]>([]);
  const [workerRemark, setWorkerRemark] = useState("");
  const [workerRemarkCopy, setWorkerRemarkCopy] = useState("");
  const [pmRemarkStatus, setPMRemarkStatus] = useState<boolean | null>();
  const [medewerkers, setMedewerkers] = useState("");
  const { showAlert } = useAlert();
  const [isProjectPopup, setIsProjectPopup] = useState(false)
  const { selectedProjectForLog, isTimerOn, selectedProjectIdForNavigation } = useTimerContext();
  const { id } = useParams<{ id: string }>();

  const { isTablet, isMobile, isDesktop } = useResponsive();
  const [openDailyRemarkDialog, setOpenDailyRemarkDialog] = useState(false);
  const [dailyRemark, setDailyRemark] = useState("");
  const [dailyRemarkCopy, setDailyRemarkCopy] = useState("");
  const [pmDailyRemarkStatus, setPMDailyRemarkStatus] = useState<boolean | null>();

  useEffect(() => {
    // task details api call
    getTaskDetails();
  }, []);
  useEffect(() => {
    // Check the current project and project Selected for log are same
    if ((isTimerOn && selectedProjectIdForNavigation !== selectedProjectForLog.id) || !isTimerOn) {
      setIsProjectPopup(true)
    }
  }, []);

  const today = new Date().toISOString().slice(0, 10);

  // Get the daily remarks if the remark_date matches today
  const todayRemark = projectData?.daily_remarks?.remark_date === today
    ? projectData?.daily_remarks
    : undefined;

  const noteText = todayRemark
    ? `${todayRemark.pm_remarks}`
    : null;

  const isLastTaskToComplete = (itemSelected: Material) => {
    if (projectData?.is_removal_task) {
      return true;
    } else {
      const isLast = projectData?.materials
        .filter(
          (item: Material) => itemSelected.material_id !== item.material_id
        )
        .reduce((result: boolean, item: Material) => {
          return !!item.material_status && result;
        }, true);
      return isLast;
    }
  };

  // worker updating remark
  const handleChange = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    setWorkerRemark(event.target.value);
  };

  // This function retrieves task details from an API, processes the response,
  // and updates the state accordingly. It handles loading states, user authentication, and error
  // management while ensuring that the current user's
  // email is excluded from the list of task workers.
  const getTaskDetails = async () => {
    try {
      setIsLoading(true);
      let userId = LocalStorageUtil.getItem(storageKeys.userId, "");
      let userToken = LocalStorageUtil.getItem(storageKeys.authToken, "");

      let jsondata = JSON.stringify({
        token: userToken,
        user_id: userId,
        task_id: id,
      });

      apiService.setAuthToken(userToken);

      const responseJson: AxiosResponse | null = await apiService.post(
        TaskDetailsApi,
        jsondata
      );

      if (
        responseJson?.hasOwnProperty("status") &&
        responseJson?.status_code === 1
      ) {
        if (!responseJson?.data || (responseJson?.data).length === 0) {
          showAlert(t("taskDetails.taskDoesNotExist"), "error");
          navigate("/planning");
          return;
        }
        let emailId = LocalStorageUtil.getItem(storageKeys.emailId, "");

        const sortedTaskList = responseJson?.data?.medewerkers.filter(
          (item: Medewerker) => {
            return !item.email.toLowerCase().includes(emailId.toLowerCase());
          }
        );

        const workerLists = await sortedTaskList
          .map((item: Medewerker) => item?.voornaam)
          .join(", ");
        // Extract data for today's daily remark
        const today = new Date().toISOString().slice(0, 10);

        // Get the daily remarks if it exists
        const todayDailyRemark = responseJson?.data?.daily_remarks?.remark_date === today
          ? responseJson?.data?.daily_remarks
          : undefined;

        setMedewerkers(workerLists);
        setProjectData(responseJson?.data);
        setTool(responseJson?.data.tools);
        setIsFlag(responseJson?.data.flag_status);
        setWorkerRemark(responseJson?.data.task_worker_remarks || "");
        setWorkerRemarkCopy(responseJson?.data.task_worker_remarks || "");
        setPMRemarkStatus(responseJson?.data.pm_remarks_status);
        setDailyRemark(todayDailyRemark?.worker_remarks || "");
        setDailyRemarkCopy(todayDailyRemark?.worker_remarks || "");
        setPMDailyRemarkStatus(todayDailyRemark?.pm_remarks_unread_status || null);
      } else if (
        responseJson?.hasOwnProperty("status") &&
        (responseJson?.status_code === 2 || responseJson?.status_code === 0)
      ) {
        LocalStorageUtil.clear();
        navigate("/login");
      } else {
        console.log("error", responseJson);
        showAlert(t("common.server_error"), "error");
      }
    } catch (error) {
      console.log(error)
      showAlert(t("common.server_error"), "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setWorkerRemark(workerRemarkCopy ?? "");
  };

  const handleOpenPMRemarkDialog = () => {
    setOpenPMRemarkDialog(true);
    if (pmRemarkStatus) {
      pmRemarkStatusUpdate();
    }
  };

  const handleClosePMRemarkDialog = () => {
    setOpenPMRemarkDialog(false);
  };

  const handleSaveRemarks = async () => {
    await workerRemarkUpdate();
    handleCloseDialog();
  };

  const handleOpenDailyRemarkDialog = () => {
    setOpenDailyRemarkDialog(true);
    if (pmDailyRemarkStatus) {
      pmDailyRemarkStatusUpdate();
    }
  };

  const handleSaveDailyRemark = async () => {
    await dailyRemarkSave();
    handleCloseDailyRemarkDialog();
  };

  const handleCloseDailyRemarkDialog = () => {
    setOpenDailyRemarkDialog(false);
    setDailyRemark(dailyRemarkCopy ?? "");
  };

  // This function updates worker remarks for a specific task by sending a POST request to the server.
  // It handles various server response statuses to provide appropriate feedback to the user,
  // including success messages and error handling for different scenarios.
  // The function also manages loading states and user authentication.
  const workerRemarkUpdate = async () => {
    setIsLoading(true);
    let userId = LocalStorageUtil.getItem(storageKeys.userId, "");
    let userToken = LocalStorageUtil.getItem(storageKeys.authToken, "");

    let jsondata = JSON.stringify({
      token: userToken,
      user_id: userId,
      task_id: id,
      worker_remarks: workerRemark,
    });

    try {
      const responseJson: AxiosResponse | null = await apiService.post(
        WorkerRemarkUpdate,
        jsondata
      );

      if (
        responseJson?.hasOwnProperty("status") &&
        responseJson?.status_code === 1
      ) {
        showAlert(t("taskDetails.workerRemarkUpdated"), "success");
        // task details api call
        getTaskDetails();
      } else if (
        responseJson?.hasOwnProperty("status") &&
        responseJson?.status_code === 0
      ) {
        LocalStorageUtil.clear();
        navigate("/login");
      } else if (
        responseJson?.hasOwnProperty("status") &&
        responseJson?.status_code === 2
      ) {
        LocalStorageUtil.clear();
        showAlert(t("taskDetails.taskDoesNotExist"), "error");
      } else if (
        responseJson?.hasOwnProperty("status") &&
        responseJson?.status_code === 3
      ) {
        LocalStorageUtil.clear();
        showAlert(t("taskDetails.invalidWorkerRemarks"), "error");
      } else if (
        responseJson?.hasOwnProperty("status") &&
        responseJson?.status_code === 4
      ) {
        LocalStorageUtil.clear();
        showAlert(t("taskDetails.workerRemarksNotSaved"), "error");
      } else {
        showAlert(t("common.server_error"), "error");
      }
    } catch (error) {
      showAlert(t("common.server_error"), "error");
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  // This function handles the status change of a flag by sending a POST request to the server.
  // It retrieves the user ID and token from local storage, constructs a JSON payload,
  // and processes the server's response to update the flag status or handle errors accordingly.
  // Loading state is managed to provide feedback during the operation.
  const onFlagStatusChange = async () => {
    setIsLoading(true);
    let userId = LocalStorageUtil.getItem(storageKeys.userId, "");
    let userToken = LocalStorageUtil.getItem(storageKeys.authToken, "");

    let jsondata = JSON.stringify({
      token: userToken,
      user_id: userId,
      project_id: projectData?.project_id,
    });

    try {
      const responseJson: AxiosResponse | null = await apiService.post(
        ActivateFlag,
        jsondata
      );

      if (
        responseJson?.hasOwnProperty("status") &&
        responseJson?.status_code === 1
      ) {
        setIsFlag(true);
      } else if (
        responseJson?.hasOwnProperty("status") &&
        (responseJson?.status_code === 2 || responseJson?.status_code === 0)
      ) {
        LocalStorageUtil.clear();
        navigate("/login");
      } else {
        showAlert(t("common.server_error"), "error");
      }
    } catch (error) {
      showAlert(t("common.server_error"), "error");
    } finally {
      setIsLoading(false);
    }
  };

  // This function updates the PM remark status by making an API call.
  // It retrieves the user ID and token from local storage, constructs a JSON payload,
  // and sends a POST request to update the status. Based on the response, it either updates
  // the state, clears local storage and navigates to the login page, or shows an error alert.
  // Loading state is managed throughout the process.
  const pmRemarkStatusUpdate = async () => {
    let userId = LocalStorageUtil.getItem(storageKeys.userId, "");
    let userToken = LocalStorageUtil.getItem(storageKeys.authToken, "");

    let jsondata = JSON.stringify({
      token: userToken,
      user_id: userId,
      task_id: id,
    });

    try {
      const responseJson: AxiosResponse | null = await apiService.post(
        PMRemarkStatusUpdate,
        jsondata
      );

      //check for status condition.
      if (
        responseJson?.hasOwnProperty("status") &&
        responseJson?.status_code === 1
      ) {
        setPMRemarkStatus(false);
      } else if (
        responseJson?.hasOwnProperty("status") &&
        (responseJson?.status_code === 0 || responseJson?.status_code === 2)
      ) {
        LocalStorageUtil.clear();
        navigate("/login");
      } else {
        showAlert(t("common.server_error"), "error");
      }
    } catch (error) {
      showAlert(t("common.server_error"), "error");
    }
  };

  const dailyRemarkSave = async () => {
    setIsLoading(true);
    let userId = LocalStorageUtil.getItem(storageKeys.userId, "");
    let userToken = LocalStorageUtil.getItem(storageKeys.authToken, "");

    let jsondata = JSON.stringify({
      token: userToken,
      user_id: userId,
      task_id: id,
      type: "daily_remarks",
      remark_id: todayRemark?.id,
      worker_remarks: dailyRemark,
    });

    try {
      const responseJson: AxiosResponse | null = await apiService.post(
        WorkerRemarkUpdate,
        jsondata
      );

      if (
        responseJson?.hasOwnProperty("status") &&
        responseJson?.status_code === 1
      ) {
        showAlert(t("taskDetails.notesUpdate"), "success");
        // task details api call
        getTaskDetails();
      } else if (
        responseJson?.hasOwnProperty("status") &&
        responseJson?.status_code === 0
      ) {
        LocalStorageUtil.clear();
        navigate("/login");
      } else if (
        responseJson?.hasOwnProperty("status") &&
        responseJson?.status_code === 2
      ) {
        LocalStorageUtil.clear();
        showAlert(t("taskDetails.taskDoesNotExist"), "error");
      } else if (
        responseJson?.hasOwnProperty("status") &&
        responseJson?.status_code === 3
      ) {
        LocalStorageUtil.clear();
        showAlert(t("taskDetails.invalidWorkerRemarks"), "error");
      } else if (
        responseJson?.hasOwnProperty("status") &&
        responseJson?.status_code === 4
      ) {
        LocalStorageUtil.clear();
        showAlert(t("taskDetails.workerRemarksNotSaved"), "error");
      } else {
        showAlert(t("common.server_error"), "error");
      }
    } catch (error) {
      showAlert(t("common.server_error"), "error");
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  const pmDailyRemarkStatusUpdate = async () => {
    let userId = LocalStorageUtil.getItem(storageKeys.userId, "");
    let userToken = LocalStorageUtil.getItem(storageKeys.authToken, "");

    let jsondata = JSON.stringify({
      token: userToken,
      user_id: userId,
      task_id: id,
      type: "daily_remarks",
      remark_id: todayRemark?.id,
    });

    try {
      const responseJson: AxiosResponse | null = await apiService.post(
        PMRemarkStatusUpdate,
        jsondata
      );

      if (
        responseJson?.hasOwnProperty("status") &&
        responseJson?.status_code === 1
      ) {
        // handle success, e.g. refresh data or update state
      } else if (
        responseJson?.hasOwnProperty("status") &&
        (responseJson?.status_code === 0 || responseJson?.status_code === 2)
      ) {
        LocalStorageUtil.clear();
        navigate("/login");
      } else {
        showAlert(t("common.server_error"), "error");
      }
    } catch (error) {
      showAlert(t("common.server_error"), "error");
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        overflow: "auto",
        my: "15px",
      }}
    >
      <CommonLoader isVisible={isLoading} />
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent={isMobile || isTablet ? "center" : "space-evenly"}
        sx={{
          width: "100%",
        }}
      >
        {/* Left panel */}
        <Box
          sx={{
            width: isMobile || isTablet ? "100%" : "47%",
            display: "flex",
            flexDirection: "column",
            alignItems: isMobile || isTablet ? "center" : "flex-start",
          }}
        >
          <Box
            sx={{
              width: isMobile || isTablet ? "90%" : "90%",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Typography
              gutterBottom
              fontFamily={"Nunito Sans Regular700"}
              sx={{
                color: theme.palette.blueColors.main,
                fontSize: isMobile ? "20px" : "22px",
                height: "auto",
              }}
            >
              {"Taak" + " "}
              {projectData !== null ? projectData?.task : ""}
              <InfoPopover
                content="Om deze taak uit te voeren dient de timer te lopen"
              />
            </Typography>

            {!isDesktop && (
              <Box
                sx={{
                  display: "flex",
                  width: "10%",
                  justifyContent: "flex-end",
                }}
              >
                {isFlag ? (
                  <Flag
                    sx={{
                      color: "red",
                      fontSize: 40,
                    }}
                  />
                ) : (
                  <IconButton
                    onClick={onFlagStatusChange}
                    sx={{
                      padding: "0px",
                    }}
                  >
                    <Flag
                      sx={{
                        color: theme.palette.primary.main,
                        fontSize: 40,
                      }}
                    />
                  </IconButton>
                )}
              </Box>
            )}
          </Box>

          {/* Left - Projectcard section */}
          <ProjectDetails
            projectData={projectData || null}
            tool={tool}
            medewerkers={medewerkers}
            isFlag={isFlag || null}
            onFlagStatusChange={() => onFlagStatusChange()}
            setIsLoading={() => setIsLoading(isLoading)}
          />
          {/* Left - Material section */}
          <MaterialCard
            projectData={projectData}
            id={id}
            tool={tool}
            isLastTaskToComplete={(item) => isLastTaskToComplete(item)}
          />
          {/* Left - Remarks section  edited.*/}
          <RemarksCard
            handleOpenPMRemarkDialog={handleOpenPMRemarkDialog}
            handleOpenDialog={handleOpenDialog}
            pmRemarkStatus={pmRemarkStatus}
            handleOpenDailyRemarkDialog={handleOpenDailyRemarkDialog}
            pmDailyRemarkStatus={pmDailyRemarkStatus}
          />

          {/* Left - PDF download section */}
          {isDesktop && <DocumentCard projectData={projectData} />}
        </Box>

        {/* Right panel (Map) */}
        <Box
          sx={{
            width: isMobile || isTablet ? "100%" : "47%",
            display: "flex",
            justifyContent: "center",
            mt: isMobile ? "25px" : "40px",
            height: "50%"
            // height: isMobile
          }}
        >
          <ImageWithZoomAndDrag
            imageUrl={projectData?.project_gt_image ?? null}
          />
        </Box>
        {!isDesktop && (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              // mt: "8px",
            }}
          >
            <DocumentCard projectData={projectData} />
          </Box>
        )}
      </Stack>

      <RemarksDialog
        open={openDialog}
        onClose={handleCloseDialog}
        onSave={handleSaveRemarks}
        onChange={handleChange}
        initialRemarks={workerRemark}
      />

      <PMRemarkDialog
        open={openPMRemarkDialog}
        onClose={handleClosePMRemarkDialog}
        remarkText={projectData?.task_remarks ?? null}
      />

      <DailyRemarkDialog
        open={openDailyRemarkDialog}
        onClose={handleCloseDailyRemarkDialog}
        onSave={handleSaveDailyRemark}
        onChange={e => setDailyRemark(e.target.value)}
        initialNotes={dailyRemark}
        noteText={noteText}
      />

      {!isLoading &&
        <TimerProjectSelectionModal
          handleClose={() => {
            setIsProjectPopup(false)
          }}
          isAnotherProject={selectedProjectForLog.id !== null && selectedProjectForLog.id !== selectedProjectIdForNavigation}
          isVisible={isProjectPopup}
          isDropdownDiabled={true}
        />}

    </Box>
  );
};

export default TaskDetailScreen;
